import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  Container,
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Radio,
  FormLabel,
  Divider,
  Snackbar,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { canUserEditField } from '../../utils/fieldVisibility';
import { useParams, useNavigate } from 'react-router-dom';
import HtmlContentField from '../../components/HtmlContentField';
import axios from 'axios';

interface Field {
  id: string;
  type: string;
  label: string;
  required?: boolean;
  description?: string;
  options?: any[];
  styling?: {
    textAlign?: 'left' | 'center' | 'right';
    fontSize?: 'small' | 'medium' | 'large';
    fontWeight?: 'normal' | 'bold';
    color?: string;
    backgroundColor?: string;
    margin?: string;
    padding?: string;
  };
  htmlContent?: string;
}

interface SubmissionData {
  _id: string;
  uniqueId: string;
  editCode: string;
  form: {
    _id: string;
    title: string;
    allowEditing?: boolean; // Whether form allows editing
    fields: any[];
  };
  data: Record<string, any>;
  status: string;
  adminNotes?: string;
  createdAt: string;
  updatedAt: string;
  editHistory?: Array<{
    editedAt: string;
    editedBy: string;
    previousData: Record<string, any>;
    changedFields: string[];
    newData?: Record<string, any>;
  }>;
}

const AdminSubmissionEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const loadSubmission = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/submissions/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        const submissionData = response.data;
        setSubmission(submissionData);
        
        // Initialize form data with current submission values
        const initialFormData: Record<string, any> = {};
        submissionData.form.fields.forEach((field: any) => {
          const currentValue = getFieldValue(field, submissionData.data);
          initialFormData[field.id] = currentValue;
        });
        setFormData(initialFormData);

      } catch (error: any) {
        console.error('Failed to load submission:', error);
        setError(error.response?.data?.message || 'Failed to load submission details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadSubmission();
    }
  }, [id]);

  const getFieldValue = (field: any, data: Record<string, any>) => {
    // Try multiple possible keys to find the field value
    const possibleKeys = [
      field.id,
      field.name,
      field._id,
      `field_${field.id}`,
      `field_${field._id}`,
      `field_${field.name}`
    ];
    
    for (const key of possibleKeys) {
      if (key && data.hasOwnProperty(key)) {
        return data[key];
      }
    }
    
    return field.type === 'checkbox' ? [] : '';
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!submission) return;

    try {
      setSaving(true);
      setError(null);

      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/submissions/${id}`,
        { data: formData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccessMessage('Submission updated successfully! Changes have been tracked in edit history.');
      
      // Navigate back to submission view to show updated data
      setTimeout(() => {
        navigate(`/admin/submissions/${id}`, { state: { refreshData: true } });
      }, 1500);

    } catch (error: any) {
      console.error('Failed to update submission:', error);
      setError(error.response?.data?.message || 'Failed to update submission');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field: any) => {
    const value = formData[field.id] || '';
    const isEditable = canUserEditField(field, null); // Use enhanced field visibility logic

    const FieldWrapper = ({ children }: { children: React.ReactNode }) => (
      <Box sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <Typography variant="subtitle2" color="textSecondary">
            {field.label}
            {field.required && <span style={{ color: 'red' }}> *</span>}
          </Typography>
          {!isEditable && (
            <Chip 
              label={field.pricing?.enabled ? "Pricing field - locked" : "Non-editable by users"} 
              size="small" 
              color="warning" 
              variant="outlined"
              sx={{ fontSize: '0.7rem' }}
            />
          )}
        </Box>
        {children}
      </Box>
    );

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
      case 'url':
        return (
          <FieldWrapper key={field.id}>
            <TextField
              fullWidth
              placeholder={field.label}
              type={field.type}
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              required={field.required}
              multiline={field.type === 'textarea'}
              rows={field.type === 'textarea' ? 4 : 1}
              helperText={field.description}
              variant="outlined"
            />
          </FieldWrapper>
        );

      case 'textarea':
        return (
          <FieldWrapper key={field.id}>
            <TextField
              fullWidth
              placeholder={field.label}
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              required={field.required}
              multiline
              rows={4}
              helperText={field.description}
              variant="outlined"
            />
          </FieldWrapper>
        );

      case 'number':
        return (
          <TextField
            fullWidth
            label={field.label}
            type="number"
            value={value}
            onChange={(e) => handleFieldChange(field.id, parseFloat(e.target.value) || 0)}
            required={field.required}
            helperText={field.description}
          />
        );

      case 'select':
        return (
          <FormControl fullWidth required={field.required}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              label={field.label}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            >
              {field.options?.map((option: any, index: number) => (
                <MenuItem key={index} value={option.value || option}>
                  {option.label || option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'radio':
        return (
          <FormControl component="fieldset" required={field.required}>
            <FormLabel component="legend">{field.label}</FormLabel>
            <RadioGroup
              value={value}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
            >
              {field.options?.map((option: any, index: number) => (
                <FormControlLabel
                  key={index}
                  value={option.value || option}
                  control={<Radio />}
                  label={option.label || option}
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'checkbox':
        const checkboxValue = Array.isArray(value) ? value : [];
        return (
          <FormControl component="fieldset" required={field.required}>
            <FormLabel component="legend">{field.label}</FormLabel>
            <FormGroup>
              {field.options?.map((option: any, index: number) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={checkboxValue.includes(option.value || option)}
                      onChange={(e) => {
                        const optionValue = option.value || option;
                        let newValue;
                        if (e.target.checked) {
                          newValue = [...checkboxValue, optionValue];
                        } else {
                          newValue = checkboxValue.filter((v: any) => v !== optionValue);
                        }
                        handleFieldChange(field.id, newValue);
                      }}
                    />
                  }
                  label={option.label || option}
                />
              ))}
            </FormGroup>
          </FormControl>
        );

      case 'date':
        return (
          <TextField
            fullWidth
            label={field.label}
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            InputLabelProps={{ shrink: true }}
            helperText={field.description}
          />
        );

      case 'file':
        return (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {field.label} {field.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              File fields cannot be edited directly. Current file: {value ? value.split('/').pop() : 'No file uploaded'}
            </Alert>
          </Box>
        );

      case 'heading':
        return (
          <Box key={field.id} sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              component="h3"
              sx={{
                textAlign: field.styling?.textAlign || 'left',
                fontSize: field.styling?.fontSize === 'small' ? '1rem' :
                          field.styling?.fontSize === 'large' ? '1.5rem' : '1.25rem',
                fontWeight: field.styling?.fontWeight || 'bold',
                color: field.styling?.color || 'inherit',
                backgroundColor: field.styling?.backgroundColor || 'transparent',
                margin: field.styling?.margin || '0',
                padding: field.styling?.padding || '0',
                mb: 1
              }}
            >
              {field.label}
            </Typography>
            {field.description && (
              <Typography variant="body2" color="textSecondary">
                {field.description}
              </Typography>
            )}
          </Box>
        );

      case 'paragraph':
        return (
          <Box key={field.id} sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                textAlign: field.styling?.textAlign || 'left',
                fontSize: field.styling?.fontSize === 'small' ? '0.875rem' :
                          field.styling?.fontSize === 'large' ? '1.125rem' : '1rem',
                fontWeight: field.styling?.fontWeight || 'normal',
                color: field.styling?.color || 'inherit',
                backgroundColor: field.styling?.backgroundColor || 'transparent',
                margin: field.styling?.margin || '0',
                padding: field.styling?.padding || '0'
              }}
            >
              {field.label}
            </Typography>
          </Box>
        );

      case 'divider':
        return (
          <Box key={field.id} sx={{ my: 2 }}>
            <Divider />
          </Box>
        );

      case 'html':
        return (
          <Box key={field.id} sx={{ mb: 2 }}>
            <HtmlContentField 
              field={field}
              formData={formData}
              submissionData={submission?.data}
            />
          </Box>
        );

      default:
        return (
          <TextField
            fullWidth
            label={field.label}
            value={value}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            required={field.required}
            helperText={field.description}
          />
        );
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/admin/submissions/${id}`)}
          sx={{ mt: 2 }}
        >
          Back to Submission
        </Button>
      </Container>
    );
  }

  if (!submission) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">Submission not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/submissions')}
          sx={{ mt: 2 }}
        >
          Back to Submissions
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/admin/submissions/${id}`)}
            variant="outlined"
          >
            Back to Submission
          </Button>
          <Box>
            <Typography variant="h4" component="h1">
              Edit Submission
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {submission.form.title} • ID: {submission.uniqueId}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <EditIcon color="primary" />
        </Box>
      </Box>

      {/* Form */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Edit Form Data
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Changes will be automatically tracked in the submission's edit history.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {submission.form.fields
                .filter((field) => field.type !== 'html') // Hide HTML content fields from admin edit view
                .map((field) => (
                <Box key={field._id}>
                  {renderField(field)}
                </Box>
              ))}
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" mt={4}>
              <Button
                variant="outlined"
                onClick={() => navigate(`/admin/submissions/${id}`)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Edit History Info */}
      {submission.editHistory && submission.editHistory.length > 0 && (
        <Box mt={3}>
          <Alert severity="info">
            This submission has been edited {submission.editHistory.length} time{submission.editHistory.length > 1 ? 's' : ''}. 
            All changes are tracked in the edit history.
          </Alert>
        </Box>
      )}

      {/* Success/Error Snackbars */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminSubmissionEdit;
